import React from "react";
import ReactDom from "react-dom";

import styles from "./planChangeModal.module.css";
import Button from "../button/button";

const formatBRL = (value) =>
  value === null || value === undefined
    ? "—"
    : value.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });

// Plano "sob consulta": sem valor de onboarding/mensal definido.
export const isOnRequestPlan = (plan) =>
  !!plan && plan.monthlyValue === null && plan.onboardingValue === null;

// No plano sob consulta (topo da tabela) o "Até X" não faz sentido — é o maior
// plano, então exibimos "A partir de X".
export const planDisplayName = (plan) =>
  plan && isOnRequestPlan(plan)
    ? plan.name.replace(/^at[eé]\s+/i, "A partir de ")
    : plan?.name;

/**
 * Modal de mudança de plano da mantenedora — etapas "confirm" e "success".
 * Componente apresentacional: o estado/saída ficam no chamador.
 *
 * Props:
 *  - step: "select" | "confirm" | "success" | null  (null = não renderiza)
 *  - fromPlan / toPlan: objetos de plano ({ name, monthlyValue, onboardingValue })
 *  - options: planos elegíveis ({ id, name, maxActiveStudents, monthlyValue })
 *             exibidos na etapa "select" para o usuário escolher.
 *  - projected: total de alunos ativos projetado (rótulo de contexto na seleção).
 *  - loading: bool — desabilita ações enquanto salva
 *  - onSelect(plan): chamado ao escolher um plano na etapa "select"
 *  - onConfirm(): chamado ao confirmar a mudança (etapa confirm)
 *  - onCancel(): chamado ao cancelar / fechar o backdrop (etapas select/confirm)
 *  - onCloseSuccess(): chamado ao fechar a etapa de sucesso
 */
const PlanChangeModal = ({
  step,
  fromPlan,
  toPlan,
  options = [],
  projected,
  loading = false,
  onSelect,
  onConfirm,
  onCancel,
  onCloseSuccess,
}) => {
  const modalRoot =
    typeof document !== "undefined"
      ? document.getElementById("modal-root")
      : null;

  if (!step || !modalRoot) return null;

  if (step === "success") {
    return ReactDom.createPortal(
      <div className={styles.backdrop} onClick={onCloseSuccess}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={`${styles.icon} ${styles.iconSuccess}`}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>

          <div className={styles.header}>
            <h3>Plano alterado com sucesso</h3>
          </div>

          <div className={styles.body}>
            <p>
              O plano da mantenedora foi alterado para{" "}
              <strong>{planDisplayName(toPlan)}</strong>.
            </p>
            <p>
              {isOnRequestPlan(toPlan)
                ? "Por se tratar de um plano sob consulta, nossa equipe entrará em contato para finalizar a contratação."
                : "O novo valor será cobrado a partir da próxima mensalidade."}
            </p>
          </div>

          <div className={styles.actions}>
            <Button
              text="Entendi"
              variation="primary"
              size="big"
              onClick={onCloseSuccess}
            />
          </div>
        </div>
      </div>,
      modalRoot
    );
  }

  if (step === "select") {
    return ReactDom.createPortal(
      <div className={styles.backdrop} onClick={onCancel}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.icon}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          <div className={styles.header}>
            <h3>Escolha um plano</h3>
            <p className={styles.subtitle}>
              {projected != null
                ? `Planos que comportam os ${projected.toLocaleString("pt-BR")} aluno(s) ativo(s) projetado(s).`
                : "Selecione o plano mais adequado para a sua rede."}
            </p>
          </div>

          <div className={styles.body}>
            <ul className={styles.planList}>
              {options.map((plan) => (
                <li key={plan.id || plan.name}>
                  <button
                    type="button"
                    className={styles.planOption}
                    onClick={() => onSelect && onSelect(plan)}
                  >
                    <span className={styles.planOptionMain}>
                      <span className={styles.planOptionName}>
                        {planDisplayName(plan)}
                      </span>
                      {plan.maxActiveStudents != null && (
                        <span className={styles.planOptionCapacity}>
                          {isOnRequestPlan(plan) ? "a partir de" : "até"}{" "}
                          {plan.maxActiveStudents.toLocaleString("pt-BR")} alunos ativos
                        </span>
                      )}
                    </span>
                    <span className={styles.planOptionPrice}>
                      {isOnRequestPlan(plan)
                        ? "Sob consulta"
                        : `${formatBRL(plan.monthlyValue)}/mês`}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.actions}>
            <Button
              text="Cancelar"
              variation="secondary"
              size="big"
              onClick={onCancel}
            />
          </div>
        </div>
      </div>,
      modalRoot
    );
  }

  // step === "confirm"
  return ReactDom.createPortal(
    <div
      className={styles.backdrop}
      onClick={loading ? undefined : onCancel}
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.icon}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
        </div>

        <div className={styles.header}>
          <h3>Confirmar mudança de plano</h3>
          <p className={styles.subtitle}>
            Revise a alteração antes de confirmar.
          </p>
        </div>

        <div className={styles.body}>
          <div className={styles.planChange}>
            <div className={styles.planBox}>
              <span className={styles.planBoxLabel}>Plano atual</span>
              <span className={styles.planBoxName}>
                {fromPlan ? planDisplayName(fromPlan) : "Nenhum"}
              </span>
              {fromPlan && !isOnRequestPlan(fromPlan) && (
                <span className={styles.planBoxPrice}>
                  {formatBRL(fromPlan.monthlyValue)}/mês
                </span>
              )}
            </div>

            <div className={styles.planArrow} aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </div>

            <div className={`${styles.planBox} ${styles.planBoxNew}`}>
              <span className={styles.planBoxLabel}>Novo plano</span>
              <span className={styles.planBoxName}>
                {planDisplayName(toPlan)}
              </span>
              {toPlan && !isOnRequestPlan(toPlan) && (
                <span className={styles.planBoxPrice}>
                  {formatBRL(toPlan.monthlyValue)}/mês
                </span>
              )}
            </div>
          </div>

          <p className={styles.info}>
            {toPlan && !isOnRequestPlan(toPlan) ? (
              <>
                O novo valor de{" "}
                <strong>{formatBRL(toPlan.monthlyValue)}/mês</strong> será
                cobrado <strong>a partir da próxima mensalidade</strong>. A
                mensalidade atual não é alterada.
              </>
            ) : (
              <>
                A alteração de plano será aplicada{" "}
                <strong>a partir da próxima mensalidade</strong>. A mensalidade
                atual não é alterada.
              </>
            )}
          </p>

          {isOnRequestPlan(toPlan) && (
            <p className={styles.note}>
              Este é um plano <strong>sob consulta</strong>. Ao confirmar, nossa
              equipe entrará em contato para finalizar a contratação.
            </p>
          )}
        </div>

        <div className={styles.actions}>
          <Button
            text="Cancelar"
            variation="secondary"
            size="big"
            onClick={onCancel}
            disabled={loading}
          />
          <Button
            text="Confirmar mudança"
            variation="primary"
            size="big"
            onClick={onConfirm}
            loading={loading}
          />
        </div>
      </div>
    </div>,
    modalRoot
  );
};

export default PlanChangeModal;
